<script lang="ts">
	import Katex from './Katex.svelte';
	import uqnSplit from './functions/uqnSplit';
	import signAssign from './functions/signAssign';
	import fractionBuilder from './functions/fractionBuilder';
	import Fraction from './functions/fractionClass';
	import polyBuilder from './functions/polyBuilder';
	//import brackets from './functions/brackets';

	export let uqn: number;

	let qn: string, yEquals: string, infinity: string;
	let initX: string, initY: string, initDydx: string;
	$: [qn, yEquals, infinity, initX, initY, initDydx] = uqnToQn(uqn);

	function uqnToQn(u:number):string[]{
		let inf = "";
		let [t,i,a,b,c,s] = uqnSplit(u);
		let xInit = "0";
		if (t==1){ // f(x)
			let q: string;
			if (i<4){ // perfect square cases
				let num: string, den: string;
				if (i<2){
					num = b.toString();
					if (i==0){
						inf = "yes";
					}
				} else{
					num = polyBuilder([b],"x",1);
				}
				if (i%2==0){
					den = polyBuilder([1,0,a*a],"x",2,false);
				} else{
					den = polyBuilder([1,0,-a*a],"x",2,false);
				}
				q = fractionBuilder(num,den);
			} else if (i==4){ // complete square case
				[a,b] = signAssign(s,a,b);
				let halfA = new Fraction(-a,2);
				halfA.simplify();
				xInit = halfA.toString();
				q = fractionBuilder(1,polyBuilder([1,a,b],"x",2,false));
			} else if (i==5){
				q = "\\cos^2 (" + polyBuilder([a],"x",1) + ")";
			} else if (i==6){
				q = "\\sin^2 (" + polyBuilder([a],"x",1) + ")";
			}
			const iY = c.toString(), iX = xInit;
			const yEq = "y=f(x).";
			q = `\\displaystyle ${fractionBuilder("\\mathrm{d}y","\\mathrm{d}x")} = ${q}.`;
			return [q,yEq,inf, iX, iY];
		} else if (t==2){ // f(y)
			let yPoly: string
			let yEq = "y=f(x).";
			let cInit = c.toString();
			let xInit = "0";
			if (i<3){ // 0: a + by, 1: sqrt(a+by), 2: (a+by)^2/3
				if (i==2){ // cuberoot
					[a,b] = signAssign(s,a,b);
					yPoly = polyBuilder([a,b],"y");
					yPoly = `( ${yPoly} )^{\\frac{2}{3}}`;
					yEq = "x=f(y)."
					let cFrac = new Fraction(b*c,3);
					cFrac = cFrac.pow(3).minus(a).divide(b);
					cInit = cFrac.toString();
				} else if (i==1){ // sqrt
					[a,b] = signAssign(s,a,b);
					yPoly = polyBuilder([a,b],"y");
					yPoly = `\\sqrt{ ${yPoly} }`;
					yEq = "x=f(y)."
					let cFrac = new Fraction(b*c,2);
					cFrac = cFrac.pow(2).minus(a).divide(b);
					const surdFrac = cFrac.multiply(b).add(a);
					cInit = surdFrac.eval() > 0? cFrac.toString() : cFrac.multiply(-1).toString();
				} else{ // i = 0: a+by
					yPoly = polyBuilder([a,-b],"y");
					let cFrac = new Fraction(a-c,b);
					cFrac.simplify();
					cInit = cFrac.toString();
					inf = "yes";
				}
			} else if (i==3 || i==4){ // perfect squares
				yEq = "x=f(y).";
				cInit = "0";
				xInit = c.toString();
				let sign = 1;
				if (i==4){
					sign = -1;
				}
				yPoly = polyBuilder([a*a,0,sign*b*b],"y")
			} else if (i==5){ // complete square
				[a,b] = signAssign(s,a,b);
				yEq = "x=f(y).";
				cInit = new Fraction(-a,2).simplify().toString();
				xInit = c.toString();
				yPoly = polyBuilder([-b,-a,-1],"y");
			} else{ // e^ay
				yPoly = `e^{ ${polyBuilder([a],"y",1)} }`;
				cInit = "0";
				xInit = new Fraction(c-1,a).simplify().toString();
			}
			const iY = cInit, iX = xInit;
			const q = `\\displaystyle ${fractionBuilder("\\mathrm{d}y","\\mathrm{d}x")} = ${yPoly}.`;
			return [q,yEq,inf,iX,iY]
		} else if (t==3){ // d2ydx2 = f(x)
			[a,b,c] = signAssign(s,a,b,c);
			// values for i==0: d2ydx2 = a
			let q = a.toString();
			let yEq = "y=f(x)."
			let yInit = c.toString();
			let dydxInit = b.toString();
			if (i==1){ // a+bx
				q = polyBuilder([a,b]);
				yInit = "1";
				dydxInit = c.toString();
			} else if (i==2){ // e^ax
				q = `e^{${polyBuilder([a],"x",1)}}`;
				dydxInit = new Fraction(1,a).simplify().add(b).toString();
				yInit = new Fraction(1,a*a).simplify().add(c).toString();
			} else if (i==3) { // cos ax
				q = `\\cos (${polyBuilder([a],"x",1)})`;
				yInit = new Fraction(-1,a*a).simplify().add(c).toString();
			} else if (i==4) { // sin ax
				q = `\\sin (${polyBuilder([a],"x",1)})`;
				dydxInit = new Fraction(-1,a).simplify().add(b).toString();				
			}
			q = `\\displaystyle \\frac{\\mathrm{d}^2y}{\\mathrm{d}x^2} = ${q}.`
			return [q,yEq,inf,"0",yInit,dydxInit];
		}
	}

</script>

<p> 
	A differential equation is given by
</p>
<p class="qnpart">
	<Katex math={qn}/>
</p>
<p>
	(a) Find the general solution of the differential equation, leaving your answer in the form
	<Katex math={yEquals}/>
</p>
<p>
	(b) Find the particular solution of the differential equation, given that
	<Katex math={"y="+initY}/>
	{#if typeof initDydx !== "undefined"}
	and
	<Katex math={"\\frac{\\mathrm{d}y}{\\mathrm{d}x}="+initDydx}/>
	{/if}
	when 
	<Katex math={"x="+initX+"."}/> 
</p>
{#if infinity}
<p>(c) State the value of <Katex math="y"/> for large values of <Katex math="x."/></p>
{/if}


